import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import type { CurationLimits } from '../../types/keywords';
import { formatShare } from './utils';

// Keep in sync with the rules the proposal flow gives the AI (RULES in import/keywords/proposals.py).
const RULES = [
  'purpose',
  'noFilters',
  'notTooNarrow',
  'notTooBroad',
  'label',
  'definition',
  'synonyms',
  'size',
  'merge',
  'cost',
] as const;

interface CurationGuidelinesProps {
  limits: CurationLimits;
  /** Start expanded, e.g. while the list is still empty. */
  defaultExpanded: boolean;
}

/** Rules and best practices for curating the keyword list, with the configured thresholds. */
export function CurationGuidelines({ limits, defaultExpanded }: CurationGuidelinesProps) {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const values = {
    ...limits,
    min_share: formatShare(limits.min_share),
    max_share: formatShare(limits.max_share),
  };

  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
      <Link component="button" variant="body2" fontWeight={500} onClick={() => setExpanded(!expanded)}>
        {expanded ? t('guidelines.hide') : t('guidelines.show')}
      </Link>
      <Collapse in={expanded}>
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, '& li': { mb: 0.5 } }}>
          {RULES.map((rule) => (
            <li key={rule}>
              <strong>{t(`guidelines.rules.${rule}.title`)}</strong> {t(`guidelines.rules.${rule}.text`, values)}
            </li>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
}
